import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import PDFViewer from './PDFViewer';
import { API_ENDPOINTS } from '../config/api';

// Add CSS styles for React Quill content rendering
const quillStyles = `
  /* React Quill Size Classes - These are the actual classes Quill generates */
  .ql-size-small {
    font-size: 0.75rem !important;
    line-height: 1.4 !important;
  }
  
  .ql-size-large {
    font-size: 1.5rem !important;
    line-height: 1.3 !important;
  }
  
  .ql-size-huge {
    font-size: 2.5rem !important;
    line-height: 1.2 !important;
    font-weight: 600 !important;
  }
  
  /* Target elements with size classes */
  .ql-editor .ql-size-small,
  .card-content .ql-size-small,
  div .ql-size-small {
    font-size: 0.75rem !important;
    line-height: 1.4 !important;
  }
  
  .ql-editor .ql-size-large,
  .card-content .ql-size-large,
  div .ql-size-large {
    font-size: 1.5rem !important;
    line-height: 1.3 !important;
  }
  
  .ql-editor .ql-size-huge,
  .card-content .ql-size-huge,
  div .ql-size-huge {
    font-size: 2.5rem !important;
    line-height: 1.2 !important;
    font-weight: 600 !important;
  }
  
  /* Target specific elements that might have size classes */
  .ql-editor p.ql-size-huge,
  .card-content p.ql-size-huge,
  div p.ql-size-huge {
    font-size: 2.5rem !important;
    line-height: 1.2 !important;
    font-weight: 600 !important;
    margin-bottom: 1.5rem !important;
  }
  
  .ql-editor p.ql-size-large,
  .card-content p.ql-size-large,
  div p.ql-size-large {
    font-size: 1.5rem !important;
    line-height: 1.3 !important;
    margin-bottom: 1.25rem !important;
  }
  
  .ql-editor p.ql-size-small,
  .card-content p.ql-size-small,
  div p.ql-size-small {
    font-size: 0.75rem !important;
    line-height: 1.4 !important;
    margin-bottom: 0.75rem !important;
  }
  
  /* Target headings with size classes */
  .ql-editor h1.ql-size-huge,
  .ql-editor h2.ql-size-huge,
  .ql-editor h3.ql-size-huge,
  .card-content h1.ql-size-huge,
  .card-content h2.ql-size-huge,
  .card-content h3.ql-size-huge,
  div h1.ql-size-huge,
  div h2.ql-size-huge,
  div h3.ql-size-huge {
    font-size: 2.5rem !important;
    line-height: 1.2 !important;
    font-weight: 600 !important;
  }
  
  .ql-editor h1.ql-size-large,
  .ql-editor h2.ql-size-large,
  .ql-editor h3.ql-size-large,
  .card-content h1.ql-size-large,
  .card-content h2.ql-size-large,
  .card-content h3.ql-size-large,
  div h1.ql-size-large,
  div h2.ql-size-large,
  div h3.ql-size-large {
    font-size: 1.5rem !important;
    line-height: 1.3 !important;
  }
  
  .ql-editor h1.ql-size-small,
  .ql-editor h2.ql-size-small,
  .ql-editor h3.ql-size-small,
  .card-content h1.ql-size-small,
  .card-content h2.ql-size-small,
  .card-content h3.ql-size-small,
  div h1.ql-size-small,
  div h2.ql-size-small,
  div h3.ql-size-small {
    font-size: 0.75rem !important;
    line-height: 1.4 !important;
  }
  
  /* Universal selector to catch any element with size classes */
  *[class*="ql-size-huge"] {
    font-size: 2.5rem !important;
    line-height: 1.2 !important;
    font-weight: 600 !important;
  }
  
  *[class*="ql-size-large"] {
    font-size: 1.5rem !important;
    line-height: 1.3 !important;
  }
  
  *[class*="ql-size-small"] {
    font-size: 0.75rem !important;
    line-height: 1.4 !important;
  }
`;

const PublicationDetail = () => {
  const { identifier } = useParams();
  const [publication, setPublication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { token } = useAuth();

  useEffect(() => {
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };

    axios.get(`${API_ENDPOINTS.PUBLICATIONS}/${identifier}`, { headers })
      .then((response) => {
        setPublication(response.data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.response?.data?.error || err.message);
        setLoading(false);
      });
  }, [identifier, token]);

  if (loading) return <div className="loading">🔄 Loading publication details...</div>;
  if (error) return <div className="error">❌ Error: {error}</div>;
  if (!publication) return <div className="error">📭 Publication not found.</div>;

  return (
    <>
      {/* Add React Quill styles for proper font rendering */}
      <style dangerouslySetInnerHTML={{ __html: quillStyles }} />
      
      <div style={{ 
        background: '#f8fafc', 
        minHeight: '100vh', 
        padding: '2rem',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '2rem',
        maxWidth: '1200px',
        margin: '0 auto 2rem auto'
      }}>
        <h2 style={{ 
          fontSize: '1.8rem', 
          fontWeight: '700', 
          color: '#1f2937',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          margin: 0
        }}>
          <span role="img" aria-label="book">📖</span>
          Publication Details
        </h2>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button 
            style={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              padding: '12px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
              transition: 'all 0.3s ease',
              width: '44px',
              height: '44px'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 20px rgba(245, 158, 11, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.3)';
            }}
            onClick={() => navigate(`/dashboard/publications/${identifier}/edit`)}
            title="Edit Publication"
          >
            <svg 
              stroke="currentColor" 
              fill="currentColor" 
              strokeWidth="0" 
              viewBox="0 0 576 512" 
              height="1em" 
              width="1em" 
              xmlns="http://www.w3.org/2000/svg" 
              style={{ fontSize: '16px' }}
            >
              <path d="M402.6 83.2l90.2 90.2c3.8 3.8 3.8 10 0 13.8L274.4 405.6l-92.8 10.3c-12.4 1.4-22.9-9.1-21.5-21.5l10.3-92.8L388.8 83.2c3.8-3.8 10-3.8 13.8 0zm162-22.9l-48.8-48.8c-15.2-15.2-39.9-15.2-55.2 0l-35.4 35.4c-3.8 3.8-3.8 10 0 13.8l90.2 90.2c3.8 3.8 10 3.8 13.8 0l35.4-35.4c15.2-15.3 15.2-40 0-55.2zM384 346.2V448H64V128h229.8c3.2 0 6.2-1.3 8.5-3.5l40-40c7.6-7.6 2.2-20.5-8.5-20.5H48C21.5 64 0 85.5 0 112v352c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48V306.2c0-10.7-12.9-16-20.5-8.5l-40 40c-2.2 2.3-3.5 5.3-3.5 8.5z"></path>
            </svg>
          </button>
          <button 
            style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              padding: '12px 20px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
              transition: 'all 0.3s ease',
              minWidth: '140px',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 20px rgba(59, 130, 246, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
            }}
            onClick={() => navigate('/dashboard/publications')}
          >
            <span role="img" aria-label="back">←</span>
            Back to List
          </button>
        </div>
      </div>
      
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '2rem',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.1)',
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        <div style={{
          display: 'grid',
          gap: '1.5rem'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}>
            <label style={{
              fontSize: '12px',
              fontWeight: '600',
              color: '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              TITLE
            </label>
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              fontSize: '16px',
              color: '#1f2937',
              fontWeight: '500'
            }}>
              {publication.title}
            </div>
          </div>
          
          {publication.subtitle && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
              <label style={{
                fontSize: '12px',
                fontWeight: '600',
                color: '#6b7280',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                SUBTITLE
              </label>
              <div style={{
                padding: '12px 16px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                fontSize: '16px',
                color: '#1f2937',
                fontWeight: '500'
              }}>
                {publication.subtitle}
              </div>
            </div>
          )}
          
          {publication.image_path && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
              <label style={{
                fontSize: '12px',
                fontWeight: '600',
                color: '#6b7280',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                PUBLICATION IMAGE
              </label>
              <div style={{
                padding: '12px 16px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'center'
              }}>
                <img 
                  src={publication.image_path} 
                  alt="Publication" 
                  style={{
                    maxWidth: '100%',
                    maxHeight: '300px',
                    borderRadius: '8px',
                    objectFit: 'contain'
                  }}
                />
              </div>
            </div>
          )}
          
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}>
            <label style={{
              fontSize: '12px',
              fontWeight: '600',
              color: '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              AUTHOR
            </label>
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              fontSize: '16px',
              color: '#1f2937',
              fontWeight: '500'
            }}>
              {publication.author || 'Unknown'}
            </div>
          </div>
          
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}>
            <label style={{
              fontSize: '12px',
              fontWeight: '600',
              color: '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              PUBLISHED DATE
            </label>
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              fontSize: '16px',
              color: '#1f2937',
              fontWeight: '500'
            }}>
              {publication.published_date 
                ? new Date(publication.published_date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })
                : 'Not specified'
              }
            </div>
          </div>
          
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}>
            <label style={{
              fontSize: '12px',
              fontWeight: '600',
              color: '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              CONTENT
            </label>
            <div style={{
              padding: '16px',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              fontSize: '16px',
              color: '#1f2937',
              fontWeight: '400',
              lineHeight: '1.6',
              minHeight: '120px'
            }}
            dangerouslySetInnerHTML={{ __html: publication.content || 'No content available.' }}
            />
          </div>

          {/* PDF Viewer Section */}
          {publication.file_path && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
              <label style={{
                fontSize: '12px',
                fontWeight: '600',
                color: '#6b7280',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                PDF DOCUMENT
              </label>
              <div style={{
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                overflow: 'hidden'
              }}>
                <PDFViewer 
                  pdfUrl={publication.file_path} 
                  title={publication.title}
                  showControls={true}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default PublicationDetail; 